import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { 
  useNotifications, 
  useNotificationsUnreadCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
  useClearAllNotifications
} from '../../lib/hooks';
import { toast } from 'sonner';
import { 
  Bell, 
  X,
  Check,
  CheckCheck,
  Trash2,
  FileText,
  CreditCard,
  BookOpen,
  MessageSquare,
  Settings,
  Loader2,
  PartyPopper,
  AlertCircle,
  Calendar,
  Trophy,
  Handshake,
  RefreshCw
} from 'lucide-react';

// Notification type to icon and color mapping
const NOTIFICATION_CONFIG = {
  welcome: { icon: PartyPopper, color: 'text-blue-500', bg: 'bg-blue-50' },
  document_uploaded: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
  document_validated: { icon: Check, color: 'text-green-500', bg: 'bg-green-50' },
  document_rejected: { icon: X, color: 'text-red-500', bg: 'bg-red-50' },
  dossier_complete: { icon: Trophy, color: 'text-green-500', bg: 'bg-green-50' },
  payment_plan_created: { icon: CreditCard, color: 'text-blue-500', bg: 'bg-blue-50' },
  payment_recorded: { icon: Check, color: 'text-green-500', bg: 'bg-green-50' },
  payment_upcoming: { icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-50' },
  payment_overdue: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' },
  course_reminder: { icon: BookOpen, color: 'text-blue-500', bg: 'bg-blue-50' },
  course_enrolled: { icon: BookOpen, color: 'text-blue-500', bg: 'bg-blue-50' },
  certificate_issued: { icon: Trophy, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  appointment_created: { icon: Calendar, color: 'text-blue-500', bg: 'bg-blue-50' },
  appointment_updated: { icon: Calendar, color: 'text-orange-500', bg: 'bg-orange-50' },
  partner_assigned: { icon: Handshake, color: 'text-blue-500', bg: 'bg-blue-50' },
  partner_status_update: { icon: RefreshCw, color: 'text-purple-500', bg: 'bg-purple-50' },
  new_message: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50' },
  message_received: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50' },
  system: { icon: Settings, color: 'text-gray-500', bg: 'bg-gray-50' },
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `Il y a ${minutes}min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const getNotificationLink = (notification) => {
  const type = notification.type || '';
  
  if (type.startsWith('document_') || type === 'dossier_complete') {
    return notification.data?.student_id ? `/dossiers/${notification.data.student_id}` : '/dossiers';
  }
  if (type.startsWith('payment_')) {
    return notification.data?.student_id ? `/payments?student=${notification.data.student_id}` : '/payments';
  }
  if (type.startsWith('course_') || type === 'certificate_issued') {
    return notification.data?.course_id ? `/courses/${notification.data.course_id}` : '/courses';
  }
  if (type.startsWith('message') || type === 'new_message') {
    return notification.data?.conversation_id ? `/messages?id=${notification.data.conversation_id}` : '/messages';
  }
  if (type.startsWith('appointment_')) {
    return '/appointments';
  }
  return null;
};

// Notification Item Component
const NotificationItem = ({ notification, onMarkRead, onDelete, onClick }) => {
  const config = NOTIFICATION_CONFIG[notification.type] || NOTIFICATION_CONFIG.system;
  const Icon = config.icon;
  const isUnread = !notification.read;
  
  const handleClick = () => {
    if (isUnread) {
      onMarkRead(notification.id);
    }
    onClick?.(notification);
  };
  
  return (
    <div 
      className={`group relative p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
        isUnread ? 'bg-white border-l-4 border-secondary' : 'bg-muted/20'
      }`}
      onClick={handleClick}
      data-testid={`notification-item-${notification.id}`}
    >
      <div className="flex gap-3">
        <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-5 h-5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${isUnread ? 'font-semibold' : 'font-normal'} text-foreground`}>
            {notification.message}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatTimeAgo(notification.created_at)}
          </p>
        </div>
        <button
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          data-testid={`delete-notification-${notification.id}`}
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
};

const NotificationCenter = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [newNotificationsCount, setNewNotificationsCount] = useState(0);
  
  // Map tabs to API categories
  const categoryMap = {
    all: undefined,
    unread: undefined, // We'll use read=false instead
    documents: 'documents',
    payments: 'payments',
    courses: 'courses',
    messages: 'messages',
  };
  
  const params = {
    category: activeTab !== 'all' && activeTab !== 'unread' ? categoryMap[activeTab] : undefined,
    read: activeTab === 'unread' ? false : undefined,
    limit: 50,
  };
  
  const { data: notificationsData, isLoading, dataUpdatedAt } = useNotifications(params);
  const { data: unreadCountData } = useNotificationsUnreadCount();
  
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();
  const clearAll = useClearAllNotifications();
  
  const notifications = notificationsData?.data || [];
  const unreadCount = unreadCountData?.count || 0;
  
  // Track new notifications
  useEffect(() => {
    if (dataUpdatedAt && notificationsData?.unread_count > 0) {
      // This would be more sophisticated in a real app
      // For now, just track if count increased
    }
  }, [dataUpdatedAt, notificationsData]);
  
  const handleMarkRead = async (notificationId) => {
    try {
      await markRead.mutateAsync(notificationId);
    } catch (error) {
      toast.error('Erreur lors du marquage');
    }
  };
  
  const handleMarkAllRead = async () => {
    try {
      await markAllRead.mutateAsync();
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur lors du marquage');
    }
  };
  
  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification.mutateAsync(notificationId);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleClearAll = async () => {
    try {
      await clearAll.mutateAsync();
      toast.success('Notifications lues supprimées');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleNotificationClick = (notification) => {
    const link = getNotificationLink(notification);
    if (link) {
      onOpenChange(false);
      navigate(link);
    }
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[420px] p-0 flex flex-col">
        <SheetHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SheetTitle className="text-lg">Notifications</SheetTitle>
              {unreadCount > 0 && (
                <Badge className="bg-secondary">{unreadCount}</Badge>
              )}
            </div>
          </div>
          <SheetDescription className="sr-only">
            Centre de notifications
          </SheetDescription>
          <div className="flex gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0 || markAllRead.isPending}
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Tout marquer lu
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              disabled={clearAll.isPending}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Effacer les lues
            </Button>
          </div>
        </SheetHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="border-b px-4">
            <TabsList className="w-full justify-start h-auto p-0 bg-transparent">
              <TabsTrigger value="all" className="text-xs px-3 py-2 data-[state=active]:bg-muted rounded-none data-[state=active]:border-b-2 data-[state=active]:border-secondary">
                Toutes
              </TabsTrigger>
              <TabsTrigger value="unread" className="text-xs px-3 py-2 data-[state=active]:bg-muted rounded-none data-[state=active]:border-b-2 data-[state=active]:border-secondary">
                Non lues
              </TabsTrigger>
              <TabsTrigger value="documents" className="text-xs px-3 py-2 data-[state=active]:bg-muted rounded-none data-[state=active]:border-b-2 data-[state=active]:border-secondary">
                Documents
              </TabsTrigger>
              <TabsTrigger value="payments" className="text-xs px-3 py-2 data-[state=active]:bg-muted rounded-none data-[state=active]:border-b-2 data-[state=active]:border-secondary">
                Paiements
              </TabsTrigger>
              <TabsTrigger value="messages" className="text-xs px-3 py-2 data-[state=active]:bg-muted rounded-none data-[state=active]:border-b-2 data-[state=active]:border-secondary">
                Messages
              </TabsTrigger>
            </TabsList>
          </div>
          
          <ScrollArea className="flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                <Bell className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-sm text-muted-foreground">Aucune notification</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={handleMarkRead}
                    onDelete={handleDelete}
                    onClick={handleNotificationClick}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationCenter;
